import type { AgentDto, LlmModelDto, LlmProviderDto } from '@shared/services/apiService/apiService';

import { isLlmModelRemoved, isLlmProviderRemoved } from '@shared/utils/removedAgentSettings';

/**
 * Pure display helpers for LLM-related titles. No hooks here.
 */

export function resolveAgentProvider(
  row: AgentDto,
  providers?: LlmProviderDto[] | null,
): LlmProviderDto | null {
  if (Array.isArray(providers)) {
    return providers.find((p) => p.id === row.llmProviderId) ?? null;
  }
  return row.llmProvider ?? null;
}

export function resolveProviderTitle(
  row: AgentDto,
  providers?: LlmProviderDto[] | null,
  t?: (key: string) => string,
): string {
  if (isLlmProviderRemoved(row, providers)) {
    return t ? t('adminMainPage.agents.llmProviderRemoved') : 'LLM provider removed';
  }
  const title = resolveAgentProvider(row, providers)?.title;
  if (title) return title;
  return t ? t('adminMainPage.agents.unknownProvider') : 'Unknown provider';
}

export function resolveModelTitle(
  row: AgentDto,
  providers?: LlmProviderDto[] | null,
  providerModels?: LlmModelDto[] | null,
  t?: (key: string) => string,
): string {
  // If provider allows custom models, always prefer custom key
  if (row.llmCustomModelKey) return row.llmCustomModelKey;

  if (isLlmModelRemoved(row, providers)) {
    return t ? t('adminMainPage.agents.llmModelRemoved') : 'LLM model removed';
  }

  const modelTitle = resolveModelSortValue(row, providers, providerModels);
  if (modelTitle) return modelTitle;

  return t ? t('adminMainPage.agents.unknownModel') : 'Unknown model';
}

export function resolveProviderSortValue(
  row: AgentDto,
  providers?: LlmProviderDto[] | null,
): string {
  if (isLlmProviderRemoved(row, providers)) return '';
  return resolveAgentProvider(row, providers)?.title ?? '';
}

export function resolveModelSortValue(
  row: AgentDto,
  providers?: LlmProviderDto[] | null,
  providerModels?: LlmModelDto[] | null,
): string {
  if (row.llmCustomModelKey) return row.llmCustomModelKey;
  if (isLlmModelRemoved(row, providers)) return '';

  // If relation already present with a title
  if (row.llmModel?.title) return row.llmModel.title;

  // If provider allows custom models but no custom key set
  if (resolveAgentProvider(row, providers)?.allowsCustomModels) return '';

  // Otherwise resolve from provider model list
  if (providerModels && row.llmModelId) {
    return providerModels.find((m) => m.id === row.llmModelId)?.title ?? '';
  }

  return '';
}
